import React, { useState } from 'react';
import Link from 'next/link';
import Accordion from 'react-bootstrap/Accordion';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Container from 'react-bootstrap/Container';
import Card from 'react-bootstrap/Card';
import SVG from 'react-inlinesvg';
import { faPhoneAlt, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import camelcaseKeys from 'camelcase-keys';
import Layout from '../components/Layout';
import OpenGraphTitle from '../components/OpenGraphTitle';
import OpenGraphDescription from '../components/OpenGraphDescription';
import Section from '../components/Section';
import PageTitle from '../components/PageTitle';
import PhoneLink from '../components/PhoneLink';
import TextColumns from '../components/TextColumns';
import AccordionIcon from '../components/AccordionIcon';
import Head from '../components/head';
import Gataware from '../SDKs/Gataware';
import { FranchiseeState } from '../context/Franchisee/franchisee.types';
import territoriesHash from '../data/territories_hash.json';
import { FranchiseeProvider } from '../context/Franchisee';
import axios from 'axios';
import Image from 'next/image';

const iconSparkles = '/images/sparkles-two.svg';
const iconNeighborhood = '/images/icon-neighborhood.svg';

export const getStaticProps = async () => {
  const response = await Gataware.getLocations()
    .then(({ data }) => JSON.parse(data))
    .then((data) =>
      data.data.filter(
        (franchisee) =>
          !franchisee.slug.includes('corporate') &&
          !franchisee.slug.includes('training') &&
          !franchisee.slug.includes('client')
      )
    );

  const franchisees = await camelcaseKeys(response);

  const franchiseesByState = [];

  territoriesHash.forEach((usState) => {
    const stateObject = {
      state: usState.name,
      franchisees: franchisees
        .filter(({ state }) => state === usState.abbreviation)
        .sort(({ name: storeNameA }, { name: storeNameB }) => {
          if (storeNameA > storeNameB) return 1;
          if (storeNameA < storeNameB) return -1;
          return 0;
        }),
    };
    franchiseesByState.push(stateObject);
  });

  return {
    props: {
      franchisees,
      franchiseesByState,
    },
  };
};

type FranchiseeTerritory = {
  state: string;
  franchisees: Array<FranchiseeState>;
};
interface LocationsPageProps {
  /* add locations page props here */
  // franchisees: Array<FranchiseeState>;
  franchiseesByState: Array<FranchiseeTerritory>;
}

const Locations: React.FC<LocationsPageProps> = ({
  // franchisees,
  franchiseesByState,
}: LocationsPageProps) => {
  // control toggle icon on header
  const [activeEventKey, setActiveEventKey] = useState('-1');
  function toggleEventKey(eventKey: string): void {
    if (activeEventKey === eventKey) {
      setActiveEventKey('-1');
    } else {
      setActiveEventKey(eventKey);
    }
  }

  return (
    <FranchiseeProvider>
      <Layout alternateFooter>
        <Head
          slug='locations'
          title='Locations | Two Maids '
          description='Find a Two Maids  store near you!'
        />
        <OpenGraphTitle title='Locations | Two Maids ' />
        <OpenGraphDescription>
          Find a Two Maids  store near you!
        </OpenGraphDescription>
        <div className='top-background-dots'></div>
        <Section>
          <PageTitle className='mb-0'>Locations</PageTitle>
        </Section>
        <Section padding='bottom'>
          <Accordion bsPrefix='locations__accordion'>
            {franchiseesByState.map((state: FranchiseeTerritory) => {
              const hasFranchises = state.franchisees.length > 0;
              if (hasFranchises) {
                return (
                  <div key={state.state}>
                    <Accordion.Toggle
                      as={Card.Header}
                      eventKey={state.state}
                      onClick={() => toggleEventKey(state.state)}
                      role="button"
                      tabIndex="0"
                      aria-expanded={activeEventKey === '0'}
                      aria-controls="faq-panel-0"
                      id="locations-header-0"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
                          e.preventDefault();
                          e.currentTarget.click();
                        }
                      }}
                    >
                      <Row className='flex-nowrap'>
                        <Col xs={9}>
                          <h2 className='h4 font-weight-normal'>
                            {state.state}
                          </h2>
                        </Col>
                        <AccordionIcon
                          eventKey={state.state}
                          currentActiveKey={activeEventKey}
                          className='col d-flex justify-content-end'
                        />
                      </Row>
                    </Accordion.Toggle>
                    <Accordion.Collapse eventKey={state.state}>
                      <Card.Body
                        id="locations-panel-0"
                        role="region"
                        aria-labelledby="locations-header-0"
                      >
                        {state.franchisees.map((franchisee) => (
                          <div
                            key={franchisee.slug}
                            className='locations__franchise py-4'
                          >
                            <Row className='w-100'>
                              <Col>
                                <h3 className='h5 locations__franchise_name'>
                                  <Link
                                    href={`/${franchisee.slug}/`}
                                    prefetch={false}
                                    passHref
                                  >
                                    <a
                                      className='underline-reveal-from-left'
                                      onClick={() => {
                                        axios
                                          .delete('/api/search-zip-code')
                                          .then((result) => {})
                                          .catch((e) => console.log(e));
                                      }}
                                    >
                                      {franchisee.name}
                                    </a>
                                  </Link>
                                </h3>
                              </Col>
                            </Row>
                            <p>
                              <a
                                href={`https://maps.google.com/maps?q=Two Maids And A Mop ${franchisee.addressLineOne} ${franchisee.city} ${franchisee.state} ${franchisee.zipCode}`}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='link-body underline-reveal-from-left franchisee-location-address'
                              >
                                {franchisee.addressLineOne}
                                {franchisee.addressLineTwo && (
                                  <>
                                    <br />
                                    {franchisee.addressLineTwo}
                                  </>
                                )}
                                <br />
                                {franchisee.city}, {franchisee.state}{' '}
                                {franchisee.zipCode}
                              </a>
                              <br />
                              <PhoneLink
                                number={franchisee.phonePrimary}
                                className='link-body underline-reveal-from-left franchisee-location-phone'
                              />
                            </p>
                            {franchisee.cities.split(';').length > 0 &&
                              franchisee.cities !== '' && (
                                <>
                                  <p>
                                    We serve the following areas:
                                  </p>
                                  <TextColumns className='text-columns-4'>
                                    {franchisee.cities
                                      .split(';')
                                      .map((city, index) => (
                                        <React.Fragment
                                          key={`${franchisee.slug}-${city}${index}`}
                                        >
                                          {city}
                                          <br />
                                        </React.Fragment>
                                      ))}
                                  </TextColumns>
                                </>
                              )}
                          </div>
                        ))}
                      </Card.Body>
                    </Accordion.Collapse>
                  </div>
                );
              }
              return (
                <Card key={state.state}>
                  <Card.Header>
                    <Row className='flex-nowrap'>
                      <Col xs={9}>
                        <h2 className='h4 font-weight-normal'>{state.state}</h2>
                      </Col>
                    </Row>
                  </Card.Header>
                </Card>
              );
            })}
          </Accordion>
        </Section>
      </Layout>
    </FranchiseeProvider>
  );
};

export default Locations;
